<template>
  <div data-testid="tpg-config-form-panel">
    <!-- ── header (sticky inside the card) ───────────────────────────────── -->
    <div class="cf-header text-subtitle-2 d-flex align-center flex-wrap ga-2 pb-1">
      <span data-testid="tpg-cf-scope-label">{{ scopeLabel }}</span>

      <v-chip
        v-if="cfChangedCount"
        size="x-small"
        color="amber-darken-2"
        variant="flat"
        :title="cfChangedCount + ' ค่าเปลี่ยนใน turn นี้'"
        data-testid="tpg-cf-changed-count"
      >{{ cfChangedCount }} เปลี่ยนแปลง turn นี้</v-chip>

      <v-icon size="16" color="grey" icon="mdi-information-outline" data-testid="tpg-cf-info" />
      <v-tooltip activator="parent" location="bottom" max-width="360">
        <div class="text-caption">
          <b>Template นี้</b> = ทุก field ที่ config ไว้ + สถานะการเก็บ (มีค่าแล้ว / กำลังถามอยู่ / ตอบไม่ครบ / ยังไม่เริ่ม / ข้าม / ไม่เข้าเงื่อนไข).<br />
          เมื่อผู้ใช้ตอบแต่ละ field ค่าจะถูก <b>normalize</b> แล้วไหลลงไปอัปเดต
          <b>“Tax JSON” ในแท็บด้านล่าง</b> ซึ่งเป็น “ข้อมูลจริง” ที่ระบบใช้คำนวณภาษี.<br />
          พูดง่าย ๆ: Template = <i>เก็บครบหรือยัง</i>, Tax JSON = <i>ค่าที่เก็บได้จริง</i> — {{ scopeHint }}<br />
          <span style="color:#ffb300">■</span> แถบเหลือง = field ที่เพิ่ม/เปลี่ยนใน turn นี้.
        </div>
      </v-tooltip>

      <v-spacer />

      <v-btn-toggle
        v-model="cfMode"
        density="compact"
        mandatory
        variant="outlined"
        divided
        data-testid="tpg-cf-mode"
      >
        <v-btn value="table" size="x-small" prepend-icon="mdi-table" data-testid="tpg-cf-mode-table">ตาราง</v-btn>
        <v-btn value="json" size="x-small" prepend-icon="mdi-code-json" data-testid="tpg-cf-mode-json">JSON</v-btn>
      </v-btn-toggle>
    </div>

    <!-- ── counts (6 chips, verbatim, fixed order, always all six) ───────── -->
    <div class="d-flex ga-1 mb-2 flex-wrap" data-testid="tpg-cf-counts">
      <v-chip
        v-for="c in countChips"
        :key="c.key"
        size="x-small"
        :color="c.color"
        :variant="c.variant"
        :title="c.value + ' ' + c.text"
        :data-testid="'tpg-cf-count-' + c.key"
      >{{ c.value }} {{ c.text }}</v-chip>
    </div>

    <!-- ── JSON mode ─────────────────────────────────────────────────────── -->
    <template v-if="cfMode === 'json'">
      <v-alert
        v-if="templateJsonTooBig"
        type="info"
        density="compact"
        variant="tonal"
        class="mb-2"
        data-testid="tpg-cf-json-large"
      >
        JSON ก้อนนี้ใหญ่ ({{ templateJsonKb }} KB) — แสดงแบบย่อเพื่อไม่ให้หน้าค้าง
        <template #append>
          <v-btn size="x-small" variant="text" data-testid="tpg-cf-json-showall" @click="jsonShowAll = true">
            แสดงทั้งหมด
          </v-btn>
        </template>
      </v-alert>

      <div class="d-flex justify-end">
        <v-btn
          icon="mdi-content-copy"
          size="x-small"
          variant="text"
          title="คัดลอก Form Template JSON"
          aria-label="คัดลอก Form Template JSON"
          data-testid="tpg-cf-json-copy"
          @click="onCopyJson"
        />
      </div>

      <pre v-if="templateJsonTooBig" class="code-block" data-testid="tpg-cf-json">{{ templateJson }}</pre>
      <pre
        v-else
        class="code-block"
        data-testid="tpg-cf-json"
      ><template v-for="(ln, i) in templateJsonLines" :key="i"><span :class="{ 'json-changed': ln.changed }">{{ ln.text }}</span>
</template></pre>
    </template>

    <!-- ── table mode (default) ──────────────────────────────────────────── -->
    <v-expansion-panels v-else v-model="openPanels" multiple class="cf-panels">
      <v-expansion-panel
        v-for="grp in groups"
        :key="grp.group"
        :data-testid="'tpg-cf-group-' + grp.group"
      >
        <v-expansion-panel-title>
          {{ groupLabel(grp) }}
          <span class="text-medium-emphasis text-caption ml-2">({{ (grp.fields || []).length }})</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <div v-if="!(grp.fields || []).length" class="text-medium-emphasis text-caption pa-2">
            — ไม่มีรายการในหมวดนี้ —
          </div>
          <table v-else class="cf-table">
            <tbody>
              <tr
                v-for="f in grp.fields"
                :key="f.fieldKey"
                :class="{ 'cf-changed': isChanged(f) }"
                :title="isChanged(f) ? 'เปลี่ยนใน turn นี้' : null"
                :data-testid="'tpg-cf-row-' + f.fieldKey"
              >
                <td class="cf-label" :title="f.label || f.fieldKey">{{ f.label || f.fieldKey }}</td>
                <td class="cf-status">
                  <v-chip
                    size="x-small"
                    :color="statusMeta(f.status).color"
                    :variant="statusMeta(f.status).variant"
                    :title="statusTitle(f)"
                    :data-testid="'tpg-cf-status-' + f.fieldKey"
                  >{{ statusMeta(f.status).label }}</v-chip>
                </td>
                <td class="cf-value">
                  <span v-if="f.value">{{ f.value }}</span>
                  <span v-else-if="f.status === 'not_applicable'" class="text-medium-emphasis">— {{ f.reason }}</span>
                  <span v-else class="text-disabled">—</span>

                  <div
                    v-if="showCustomerInput(f)"
                    class="cf-cust"
                    :data-testid="'tpg-cf-customer-input-' + f.fieldKey"
                  >ลูกค้าพิมพ์: “{{ f.customerInput }}”</div>
                </td>
              </tr>
            </tbody>
          </table>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
// Form Template panel of the Tax Playground V2 console (S3-P4 · task 4-A).
//
// Ported from the POC `ConfigFormPanel.vue` (Vue 3 + Vuetify 3, TypeScript +
// Composition API) into this repo's house style: plain JavaScript + Options API.
//
// 🔴 THREE RULES THIS FILE EXISTS TO ENFORCE:
//
// 1. `counts` IS RENDERED VERBATIM (TC-S3P4-63). The six chips come from
//    `COUNTS_ORDER` (imported from `utils/taxPlaygroundV2Counts.js` — never
//    re-declared here), always render all six even at 0, in a FIXED order.
//    `counts[k] ?? 0` happens at RENDER time only; the `counts` object itself is
//    never touched, never re-tallied, never "repaired".
//    🔴 The import is from the dependency-free constants module, NOT from
//    `utils/taxPlaygroundV2Store.js`: this panel is also mounted by the READ-ONLY
//    Tax Log V2 viewer, and the store pulls in `services/taxPlaygroundV2Api`
//    (POST /chat, POST /reset) plus a module-level `reactive` singleton. Do not
//    "tidy" the import back to the store.
//
// 2. `counts` KEYS ARE snake_case ON THE WIRE — `counts['not_started']`.
//    `counts.notStarted` is always `undefined` (`[JsonPropertyName]` pins them on
//    `ConfigFormV2CountsDto`).
//
// 3. `ConfigFormV2FieldDto` IS FLAT. It carries exactly: fieldKey,
//    questionItemId, label, group, kind, status, value, customerInput, reason,
//    sortOrder, isRequired, visibleWhen, isComplete. The POC's nested
//    `subItems`/`items`/`expenseMode`/`actualExpense`/`rate`/`clauseTag` blocks do
//    NOT exist in this slice and were deliberately dropped — porting them would
//    produce dead code reading `undefined` forever.
import { COUNTS_ORDER } from '../utils/taxPlaygroundV2Counts';

/**
 * 6 field statuses. `partial_answer` ("ตอบไม่ครบ", orange tonal) is the one the
 * POC lacked — §UX ruling A keeps it separate because folding it into `pending`
 * would break "exactly one pending per turn" (TC-06) and folding it into
 * `not_started` would hide that a value WAS partially captured.
 */
const STATUS_META = {
  answered: { label: 'มีค่าแล้ว', color: 'green', variant: 'flat' },
  pending: { label: 'กำลังถามอยู่', color: 'green', variant: 'tonal' },
  not_started: { label: 'ยังไม่เริ่ม', color: 'grey', variant: 'outlined' },
  not_applicable: { label: 'ไม่เข้าเงื่อนไข', color: 'amber-darken-2', variant: 'outlined' },
  skipped: { label: 'ข้าม', color: 'blue-grey', variant: 'tonal' },
  partial_answer: { label: 'ตอบไม่ครบ', color: 'orange', variant: 'tonal' },
};

/** Labels of the counts row — deliberately shorter than the status labels. */
const COUNT_CHIP_META = {
  answered: { text: 'มีค่า', color: 'green', variant: 'flat' },
  partial_answer: { text: 'ตอบไม่ครบ', color: 'orange', variant: 'tonal' },
  not_started: { text: 'ยังไม่เริ่ม', color: 'grey', variant: 'outlined' },
  not_applicable: { text: 'N/A', color: 'amber-darken-2', variant: 'outlined' },
  skipped: { text: 'ข้าม', color: 'blue-grey', variant: 'tonal' },
  pending: { text: 'กำลังถามอยู่', color: 'green', variant: 'tonal' },
};

/**
 * Thai group labels. The backend already resolves `groupLabel` on every group
 * (`ResolveGroupLabel`), so this map is only the FALLBACK for an empty/raw code:
 * `g.groupLabel || GROUP_LABEL[g.group] || g.group` — the server always wins.
 */
const GROUP_LABEL = {
  PERSONAL: 'ข้อมูลส่วนตัว',
  INCOME: 'รายได้',
  ALLOWANCE: 'ค่าลดหย่อน (Allowance)',
  DEDUCTION: 'ค่าลดหย่อน/หักภาษี (Deduction)',
  SPOUSE: 'คู่สมรส',
  SPOUSE_INCOME: 'รายได้คู่สมรส',
  SUMMARY: 'สรุป',
};

const PARTIAL_DEFAULT_REASON = 'เก็บค่าได้บางส่วน — ยังขาดข้อมูลของข้อนี้';
const JSON_BIG_BYTES = 200 * 1024;

/** Drop null/undefined props at DISPLAY time — the wire intentionally emits them. */
function pruneNulls(value) {
  if (Array.isArray(value)) return value.map(pruneNulls);
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      if (value[k] === null || value[k] === undefined) return;
      out[k] = pruneNulls(value[k]);
    });
    return out;
  }
  return value;
}

export default {
  name: 'TaxPlaygroundConfigFormPanelV2',
  props: {
    /** `{ counts, groups[] }` — the caller guarantees it is not null. */
    configForm: { type: Object, default: null },
    /** `configForm.{group lowercased}.{FieldKey}.{status|value}` */
    changedPaths: { type: Array, default: () => [] },
    /**
     * 🔴 SCOPE WORDING — additive, and the DEFAULTS ARE THE PLAYGROUND'S ORIGINAL
     * STRINGS, so `TaxPlaygroundV2.vue` renders byte-identically without passing
     * anything. They exist because this panel is REUSED by the read-only Tax Log
     * V2 viewer, where a production session has NO per-turn snapshot: the panel
     * there shows the form as it stands NOW, and hard-coding "ณ turn นี้" put two
     * contradictory scope claims one above the other. Whoever mounts the panel
     * owns the claim about WHICH point in time these values describe.
     */
    scopeLabel: { type: String, default: 'Form Template (ณ turn นี้)' },
    /** Tail of the tooltip's "Template vs Tax JSON" sentence — same rule as `scopeLabel`. */
    scopeHint: { type: String, default: 'ทั้งสอง sync กันทุก turn.' },
    // 🔴 There is no `expanded` prop any more. It existed ONLY so the desktop
    // inspector could swap its pinned-form/tabs split 55/45 ⟷ 100/0; the panel is
    // a tab on every breakpoint now and always owns the full column, so the prop —
    // and the `mdi-arrow-expand-vertical` button that drove it — had nothing left
    // to do. Do not re-add either without a second pane to trade height with.
  },
  emits: ['copy'],
  data() {
    return {
      cfMode: 'table', // B8 / TC-64 — the table is ALWAYS the default view
      openPanels: [0],
      jsonShowAll: false,
    };
  },
  computed: {
    groups() {
      return (this.configForm && this.configForm.groups) || [];
    },
    counts() {
      return (this.configForm && this.configForm.counts) || {};
    },
    /** 🔴 Fixed order from the store, all six always present, `?? 0` at render time only. */
    countChips() {
      return COUNTS_ORDER.map((k) => ({
        key: k,
        value: this.counts[k] ?? 0,
        text: (COUNT_CHIP_META[k] && COUNT_CHIP_META[k].text) || k,
        color: (COUNT_CHIP_META[k] && COUNT_CHIP_META[k].color) || 'grey',
        variant: (COUNT_CHIP_META[k] && COUNT_CHIP_META[k].variant) || 'outlined',
      }));
    },
    changedSet() {
      return new Set(this.changedPaths || []);
    },
    /** TC-65 counts `changedPaths.length` itself — not a `configForm.`-filtered subset. */
    cfChangedCount() {
      return (this.changedPaths || []).length;
    },
    templateJson() {
      const obj = {};
      this.groups.forEach((g) => {
        const bucket = {};
        (g.fields || []).forEach((f) => {
          const entry = { status: f.status, value: f.value, reason: f.reason };
          if (f.customerInput != null) entry.customerInput = f.customerInput;
          if (f.isComplete != null) entry.isComplete = f.isComplete;
          bucket[f.fieldKey] = pruneNulls(entry);
        });
        obj[g.group] = bucket;
      });
      return JSON.stringify(obj, null, 2);
    },
    templateJsonKb() {
      return Math.round(this.templateJson.length / 1024);
    },
    /** L14 (ข) — a huge blob is shown raw (no per-line highlight) so the tab cannot hang. */
    templateJsonTooBig() {
      return this.templateJson.length > JSON_BIG_BYTES && !this.jsonShowAll;
    },
    templateJsonLines() {
      if (this.templateJsonTooBig) return [];
      // A fieldKey is the 3rd segment of `configForm.{group}.{FieldKey}.{prop}`.
      const changedKeys = new Set(
        (this.changedPaths || []).map((p) => String(p || '').split('.')[2]).filter(Boolean),
      );
      return this.templateJson.split('\n').map((text) => {
        const m = text.match(/^\s*"([^"]+)"\s*:\s*\{\s*$/);
        return { text, changed: Boolean(m && changedKeys.has(m[1])) };
      });
    },
  },
  methods: {
    /** Server label wins; the FE map is only a fallback for an empty/raw code. */
    groupLabel(g) {
      return (g && (g.groupLabel || GROUP_LABEL[g.group] || g.group)) || '';
    },
    fieldBase(f) {
      return `configForm.${String((f && f.group) || '').toLowerCase()}.${f && f.fieldKey}`;
    },
    isChanged(f) {
      const base = this.fieldBase(f);
      return this.changedSet.has(`${base}.status`) || this.changedSet.has(`${base}.value`);
    },
    /** Never throws on an unknown status — the panel must survive a schema drift. */
    statusMeta(status) {
      return STATUS_META[status] || { label: String(status || '—'), color: 'grey', variant: 'outlined' };
    },
    /** A1 — colour is never the only signal: every chip carries its full text on hover. */
    statusTitle(f) {
      const meta = this.statusMeta(f && f.status);
      let reason = (f && f.reason) || '';
      if (!reason && f && f.status === 'partial_answer') reason = PARTIAL_DEFAULT_REASON;
      return reason ? `${meta.label} — ${reason}` : meta.label;
    },
    /** A3 — the raw customer text belongs to `answered` fields only (never `partial_answer`). */
    showCustomerInput(f) {
      return Boolean(f && f.status === 'answered' && f.customerInput);
    },
    onCopyJson() {
      this.$emit('copy', { text: this.templateJson, label: 'Form Template JSON' });
    },
  },
};
</script>

<style scoped>
.cf-header {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}

.cf-panels :deep(.v-expansion-panel-text__wrapper) {
  padding: 4px 8px;
}

.cf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.cf-table td {
  padding: 3px 6px;
  vertical-align: top;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* Thai has no inter-word spaces, so the browser cannot break a long label
   without `overflow-wrap: anywhere` — without it the column overflows (A6). */
.cf-label {
  width: 42%;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.cf-status {
  width: 22%;
  white-space: nowrap;
}

.cf-value {
  width: 36%;
  word-break: break-word;
}

.cf-cust {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin-top: 2px;
  word-break: break-word;
}

.cf-changed {
  background: rgba(255, 193, 7, 0.22);
}

.cf-changed .cf-label {
  border-left: 2px solid #ffb300;
  padding-left: 8px;
}

.code-block {
  font-family: monospace;
  font-size: 12px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  background: #0f1115;
  color: #dbe2ea;
  border-radius: 6px;
  padding: 8px;
  max-height: 34vh;
  overflow: auto;
}

.json-changed {
  display: inline-block;
  width: 100%;
  background: rgba(255, 193, 7, 0.28);
  border-left: 2px solid #ffb300;
  border-radius: 2px;
}

/* Mobile (xs): 2 lines per row (label + chip / value) — NEVER a horizontal scroll (A6). */
@media (max-width: 599.98px) {
  .cf-table td {
    display: block;
    width: 100% !important;
    border-bottom: none;
  }

  .cf-table tr {
    display: block;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 4px 0;
  }

  .cf-label,
  .cf-status {
    display: inline-block !important;
    width: auto !important;
  }
}
</style>
